import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Input, Spin, message } from 'antd';
import { makePostCall } from '../utils/helper';
import './StudyMergeModal.css';

interface StudyMergeModalProps {
  visible: boolean;
  selectedStudies: any[]; // At least one study selected from table
  onCancel: () => void;
  onSuccess: () => void;
}

const StudyMergeModal: React.FC<StudyMergeModalProps> = ({
  visible,
  selectedStudies,
  onCancel,
  onSuccess,
}) => {
  const [allStudies, setAllStudies] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [parentStudy, setParentStudy] = useState<any | null>(null);
  const [childStudies, setChildStudies] = useState<any[]>([]);
  const [commonDescription, setCommonDescription] = useState('');
  const [draggedStudy, setDraggedStudy] = useState<any | null>(null);

  // Load all studies for the patient/order when modal opens
  useEffect(() => {
    if (visible && selectedStudies.length > 0) {
      const firstStudy = selectedStudies[0];
      const patientId = firstStudy.patient?.pat_pin;
      const orderId = firstStudy.po_his_ord_no;

      if (!patientId || !orderId) {
        message.error('Missing patient ID or order ID');
        return;
      }

      setLoading(true);
      makePostCall('/get-studies-for-merge', { patientId, orderId })
        .then(res => {
          const studies = res.data?.data || [];
          setAllStudies(studies);
          setParentStudy(null);
          setChildStudies([]);
          setCommonDescription('');
        })
        .catch(error => {
          console.error('Error fetching studies:', error);
          message.error('Failed to load studies for merge');
          setAllStudies([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [visible, selectedStudies]);

  // Compact table columns for available studies
  const availableColumns = [
    {
      dataIndex: 'po_acc_no',
      title: 'Acc #',
      key: 'po_acc_no',
      width: 80,
      render: (text: any, record: any) => record.po_acc_no || '-',
    },
    {
      dataIndex: 'po_diag_desc',
      title: 'Description',
      key: 'po_diag_desc',
      render: (text: any, record: any) => record.po_diag_desc || '-',
    },
    {
      dataIndex: 'study_dt_tm',
      title: 'Scan Date',
      key: 'study_dt_tm',
      width: 220,
      render: (text: any, record: any) => {
        const date = record.pacs_study?.ps_study_date_time;
        return date ? new Date(date).toLocaleDateString() + ' ' + new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-';
      },
    },
  ];

  // Compact table columns for parent/child
  const assignmentColumns = [
    {
      dataIndex: 'po_acc_no',
      title: 'Acc #',
      key: 'po_acc_no',
      width: 80,
      render: (text: any, record: any) => record.po_acc_no || '-',
    },
    {
      dataIndex: 'po_diag_desc',
      title: 'Description',
      key: 'po_diag_desc',
      render: (text: any, record: any) => record.po_diag_desc || '-',
    },
  ];

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, study: any) => {
    setDraggedStudy(study);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Drop to parent table
  const handleDropParent = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedStudy) {
      // Remove from available if it was there
      setAllStudies(prev => prev.filter(s => s.pacs_ord_id !== draggedStudy.pacs_ord_id));
      // Remove from children if it was there
      setChildStudies(prev => prev.filter(s => s.pacs_ord_id !== draggedStudy.pacs_ord_id));
      // Set as parent
      setParentStudy(draggedStudy);
      setDraggedStudy(null);
    }
  };

  // Drop to child table
  const handleDropChild = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedStudy) {
      // If it was the parent, move it back to available
      if (parentStudy?.pacs_ord_id === draggedStudy.pacs_ord_id) {
        setParentStudy(null);
      }
      // Remove from available
      setAllStudies(prev => prev.filter(s => s.pacs_ord_id !== draggedStudy.pacs_ord_id));
      // Add to children if not already there
      if (!childStudies.find(s => s.pacs_ord_id === draggedStudy.pacs_ord_id)) {
        setChildStudies(prev => [...prev, draggedStudy]);
      }
      setDraggedStudy(null);
    }
  };

  // Remove from parent/child back to available
  const handleRemoveFromParent = () => {
    if (parentStudy) {
      setAllStudies(prev => [...prev, parentStudy]);
      setParentStudy(null);
    }
  };

  const handleRemoveFromChild = (study: any) => {
    setChildStudies(prev => prev.filter(s => s.pacs_ord_id !== study.pacs_ord_id));
    setAllStudies(prev => [...prev, study]);
  };

  const handleMergeConfirm = async () => {
    if (!parentStudy) {
      message.error('Please drag a study to the Parent table.');
      return;
    }
    if (childStudies.length === 0) {
      message.error('Please drag at least one study to the Child table.');
      return;
    }

    const parentUid = parentStudy.pacs_study?.ps_study_uid;
    const childUids = childStudies
      .map(s => s.pacs_study?.ps_study_uid)
      .filter(Boolean);

    if (!parentUid || childUids.length === 0) {
      message.error('Study UIDs are missing.');
      return;
    }

    setMerging(true);
    try {
      const response = await makePostCall('/merge-studies', {
        parentUid,
        childUid: childUids,
        studyDescription: commonDescription || undefined,
      });

      if (response?.data?.success !== false) {
        message.success('Studies merged successfully.');
        onSuccess?.();
        onCancel();
      } else {
        message.error(
          response?.data?.message || 'Failed to merge studies. Please try again.'
        );
      }
    } catch (error: any) {
      console.error('Merge error:', error);
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to merge studies. Please try again.';
      message.error(errorMsg);
    } finally {
      setMerging(false);
    }
  };

  if (!visible || selectedStudies.length === 0) {
    return null;
  }

  return (
    <Modal
      title="Merge Studies"
      open={visible}
      onCancel={onCancel}
      className="study-merge-modal compact"
      width={1200}
      destroyOnClose
      closable={!merging}
      maskClosable={!merging}
      keyboard={!merging}
      footer={null}
      bodyStyle={{ padding: '12px' }}
    >
      <Spin spinning={loading || merging} tip={merging ? 'Merging studies...' : 'Loading studies...'} size="large">
        <div className="merge-modal-content compact">
          {/* Available Studies Table */}
          <div className="merge-section compact">
            <div className="merge-section-title compact">Drag studies to assign (Patient ID: {selectedStudies[0]?.patient?.pat_pin} | Order: {selectedStudies[0]?.po_his_ord_no})</div>
            <div className="merge-table-wrap compact">
              <Table
                className="merge-table compact"
                tableLayout="fixed"
                size="small"
                pagination={false}
                columns={availableColumns}
                dataSource={allStudies.map((s, idx) => ({ ...s, key: s.pacs_ord_id || idx }))}
                rowKey="key"
                onRow={(record) => ({
                  draggable: true,
                  onDragStart: (e) => handleDragStart(e, record),
                })}
                style={{ cursor: 'grab' }}
              />
            </div>
          </div>

          {/* Drag & Drop Panels */}
          <div className="merge-panels-container compact">
            {/* Child Studies (Left) */}
            <div className="merge-drop-panel child">
              <div className="merge-panel-header compact">Child Studies (Drag here)</div>
              <div
                className="merge-drop-zone"
                onDragOver={handleDragOver}
                onDrop={handleDropChild}
              >
                {childStudies.length === 0 ? (
                  <div className="empty-drop-zone">Drop studies here</div>
                ) : (
                  <Table
                    className="merge-assignment-table compact"
                    tableLayout="fixed"
                    size="small"
                    pagination={false}
                    columns={[
                      ...assignmentColumns,
                      {
                        dataIndex: 'remove',
                        title: '',
                        key: 'remove',
                        width: 30,
                        render: (text: any, record: any) => (
                          <Button
                            type="link"
                            size="small"
                            danger
                            onClick={() => handleRemoveFromChild(record)}
                          >
                            ✕
                          </Button>
                        ),
                      },
                    ]}
                    dataSource={childStudies.map((s, idx) => ({ ...s, key: s.pacs_ord_id || idx }))}
                    rowKey="key"
                    onRow={(record) => ({
                      draggable: true,
                      onDragStart: (e) => handleDragStart(e, record),
                    })}
                  />
                )}
              </div>
            </div>

            {/* Parent Study (Right) */}
            <div className="merge-drop-panel parent">
              <div className="merge-panel-header compact">Parent Study (Drag here)</div>
              <div
                className="merge-drop-zone"
                onDragOver={handleDragOver}
                onDrop={handleDropParent}
              >
                {!parentStudy ? (
                  <div className="empty-drop-zone">Drop parent study here</div>
                ) : (
                  <Table
                    className="merge-assignment-table compact"
                    tableLayout="fixed"
                    size="small"
                    pagination={false}
                    columns={[
                      ...assignmentColumns,
                      {
                        dataIndex: 'remove',
                        title: '',
                        key: 'remove',
                        width: 30,
                        render: () => (
                          <Button
                            type="link"
                            size="small"
                            danger
                            onClick={handleRemoveFromParent}
                          >
                            ✕
                          </Button>
                        ),
                      },
                    ]}
                    dataSource={[{ ...parentStudy, key: parentStudy.pacs_ord_id }]}
                    rowKey="key"
                    onRow={(record) => ({
                      draggable: true,
                      onDragStart: (e) => handleDragStart(e, record),
                    })}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Common Study Description */}
          <div className="merge-section compact" style={{ marginTop: '8px' }}>
            <div className="merge-section-title compact">Common Study Description</div>
            <Input.TextArea
              rows={2}
              value={commonDescription}
              onChange={e => setCommonDescription(e.target.value)}
              placeholder="Enter merged description"
              style={{ fontSize: '12px', padding: '6px 8px' }}
            />
          </div>

          {/* Footer Actions */}
          <div className="merge-modal-actions compact">
            <Button size="small" onClick={onCancel} disabled={merging || loading}>
              Cancel
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={handleMergeConfirm}
              disabled={!parentStudy || childStudies.length === 0 || merging || loading}
              loading={merging}
            >
              Merge
            </Button>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default StudyMergeModal;
